import React, { useState } from "react";
import axios from "axios";
import JSZip from "jszip";
import FormUsersList from "../../teams/FormUsersList";
import DownloadDropdown from "./DownloadDropdown";

const DashboardHeader = ({
    searchProcessingStatus,
    isProcessing,

    showDiscoveryAccess,
    setShowDiscoveryAccess,
    selectedUserIds,
    onUserSelection,
    onSaveAccess,
    ownerEmail,
    apiBaseUrl,

    onRunSearch,
    onRefresh,
    onBack,
    backLabel,
    label,
    // Add missing props required for download functionality
    search,
    selectedPmids = [],
    headers = {},
    setErrorMessage = () => {},
    setSuccessMessage = () => {},
}) => {
    const API_BASE_URL = "/pceapi/slr/discovery"
    const isNotBegun = searchProcessingStatus === null || searchProcessingStatus === "NOT_BEGUN";
    const isRunning = searchProcessingStatus === "RUNNING";
    const isCompleted = searchProcessingStatus === "COMPLETED";

    const [downloadType, setDownloadType] = useState("abstract");
    const [isDownloading, setIsDownloading] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");

    const handleDownload = async () => {
        if (!selectedPmids || selectedPmids.length === 0) {
            setErrorMessage("Please select at least one paper to download.");
            return;
        }

        setIsDownloading(true);

        try {
            if (downloadType === "abstract") {
                setProcessingMessage("Downloading abstracts CSV...");
                const res = await axios.post(
                    `${API_BASE_URL}/download`,
                    {
                        discovery_id: search?.id,
                        selected_pmids: selectedPmids,
                        type: "abstract",
                    },
                    { headers, responseType: "blob" }
                );

                const blob = new Blob([res.data], { type: "text/csv" });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `abstracts_${search?.id || "download"}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                setSuccessMessage(`Downloaded ${selectedPmids.length} abstracts successfully.`);
            } else {
                setProcessingMessage("Fetching PDF links...");
                const res = await axios.post(
                    `${API_BASE_URL}/download`,
                    {
                        discovery_id: search?.id,
                        selected_pmids: selectedPmids,
                        type: "full_text",
                    },
                    { headers }
                );

                if (res.data?.status !== "success") {
                    setErrorMessage(res.data?.message || "Failed to get PDF links.");
                    return;
                }

                const pdfLinks = res.data?.data?.pdf_links || [];
                setProcessingMessage(`Downloading ${pdfLinks.length} PDFs...`);

                const fetchResults = await Promise.allSettled(
                    pdfLinks.map(({ pmid, title, url }) =>
                        fetch(url)
                            .then((r) => r.arrayBuffer())
                            .then((buf) => ({ pmid, title, buf }))
                    )
                );

                setProcessingMessage("Zipping PDFs...");
                const zip = new JSZip();
                let added = 0;

                fetchResults.forEach((result) => {
                    if (result.status === "fulfilled") {
                        const { pmid, title, buf } = result.value;
                        const safeTitle = (title || pmid)
                            .replace(/[^a-zA-Z0-9_\- ]/g, "")
                            .trim()
                            .slice(0, 80);
                        zip.file(`${safeTitle}_${pmid}.pdf`, buf);
                        added++;
                    }
                });

                if (added === 0) {
                    setErrorMessage("Failed to download any PDFs.");
                    return;
                }

                const zipBlob = await zip.generateAsync({ type: "blob" });
                const zipUrl = window.URL.createObjectURL(zipBlob);
                const link = document.createElement("a");
                link.href = zipUrl;
                link.download = `full_texts_${search?.id || "download"}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(zipUrl);

                setSuccessMessage(`Downloaded ${added} PDF(s) as ZIP.`);
            }
        } catch (err) {
            console.error("Download error:", err);
            setErrorMessage("Failed to download.");
        } finally {
            setIsDownloading(false);
            setProcessingMessage("");
        }
    };

    return (
        <div className="d-flex justify-content-between align-items-center mb-2">
            <p className="ev-med-p-dark-blue mb-0">{label || "Discovery Dashboard"}</p>

            <div className="d-flex gap-2 justify-content-end align-items-center">

                <div className="d-flex align-items-center btn-group custom-btn-group-outer">
                    {isNotBegun && (
                        <span className="btn genbi-custom-btn-group-inner ev-message-card5 ev-btn-lightblue-fill-round ev-btn-small ev-radius-large">
                            Not Begun
                        </span>
                    )}

                    {isRunning && (
                        <span className="btn genbi-custom-btn-group-inner ev-message-card6 ev-btn-yellow-fill-round ev-btn-small ev-radius-large">
                            Running
                        </span>
                    )}

                    {isCompleted && (
                        <span className="btn genbi-custom-btn-group-inner ev-btn-blue-fill-round ev-btn-small ev-radius-large">
                            Completed
                        </span>
                    )}

                    {!isNotBegun && (
                    <button
                        type="button"
                        className="btn genbi-custom-btn-group-inner ev-btn-gradient-fill-round ev-btn-small ev-bold-med"
                        onClick={onRunSearch}
                        disabled={isRunning || isProcessing}
                    >
                        <i className="bi bi-play-fill"></i> Run Search
                    </button>
                    )}
                </div>

                <div className="d-flex gap-2 position-relative">
                    <button
                        type="button"
                        className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-med"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowDiscoveryAccess((prev) => !prev);
                        }}
                    >
                        <i className="bi bi-people-fill"></i> Discovery Access
                    </button>

                    {showDiscoveryAccess && (
                        <div
                            className="ev-card-4 p-2 position-absolute top-100 end-0"
                            style={{ zIndex: 1050, width: 600 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="d-flex justify-content-between align-items-center mb-0 px-2 mt-1">
                                <p className="ev-reg-p-dark-blue ev-bold-med mb-0">
                                    Provide User Access
                                </p>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowDiscoveryAccess(false)}
                                />
                            </div>

                            <FormUsersList
                                apiBaseUrl={apiBaseUrl}
                                selectedUsers={selectedUserIds}
                                onSelectUser={onUserSelection}
                                ownerEmail={ownerEmail}
                            />

                            <div className="d-flex justify-content-end align-items-center mt-0">
                                <button
                                    type="button"
                                    className="ev-btn-blue-fill-round ev-btn-small"
                                    onClick={onSaveAccess}
                                    disabled={selectedUserIds.length === 0}
                                >
                                    Save Access
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Download Dropdown Integration */}
                <DownloadDropdown
                    downloadType={downloadType}
                    onTypeChange={setDownloadType}
                    onDownload={handleDownload}
                    disabled={selectedPmids.length === 0 || isDownloading}
                    isLoading={isDownloading}
                />

                <button
                    type="button"
                    className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-med"
                    onClick={onRefresh}
                    disabled={isProcessing}
                >
                    <i className="bi bi-arrow-repeat"></i> Refresh
                </button>

                <button
                    type="button"
                    className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-med"
                    onClick={onBack}
                >
                    {backLabel || "Back to Literature Discovery"}
                </button>
            </div>
        </div>
    );
};

export default DashboardHeader;